import type { VadDetectionResult } from "@/runtime/vad-runtime";

// Energy-based VAD with adaptive noise floor and hangover, batch (operates on
// a Float32Array). Equivalent algorithm to the AudioWorklet processor at
// `bench/experiments/07-vad-worklet-energy/vad-worklet.js`, validated on
// iPhone Safari 2026-04-26 (`docs/09-lane-isolation-plan.md` § VAD).
//
// This is the iPhone-safe default. The Silero+ORT path
// (`vad-runtime.ts:detectSpeechWithSilero`) remains as an experimental
// quality tier but is NOT used for the iPhone install/smoke flow because
// `ort.InferenceSession.create()` triggers iOS jetsam ~30 s later.
//
// Why batch (not streaming) here: the existing runtime path collects mic
// audio for an entire turn and then asks "what part of this is speech?".
// The streaming worklet form is the right shape for live barge-in; that
// will be wired in a later step alongside ConversationSession changes.

const SAMPLE_RATE = 16_000;
const FRAME_MS = 20;
const FRAME_SAMPLES = Math.round((FRAME_MS * SAMPLE_RATE) / 1000); // 320
const POSITIVE_MARGIN_DB = 10;
const NEGATIVE_MARGIN_DB = 6;
const MIN_SPEECH_MS = 220;
const REDEMPTION_MS = 450;
const ABSOLUTE_SILENCE_DB = -58;
const ABSOLUTE_SPEECH_FLOOR_DB = -46;
const NOISE_FLOOR_ATTACK_ALPHA = 0.05;
const NOISE_FLOOR_RELEASE_ALPHA = 0.005;
// Pre-pad / post-pad in frames so trimmed audio doesn't clip syllables.
const START_PAD_FRAMES = Math.round(300 / FRAME_MS); // 300 ms
const END_PAD_FRAMES = Math.round(200 / FRAME_MS); // 200 ms

type EnergyVadState = "silence" | "possible_speech" | "speech" | "redemption";

export interface EnergyVadDiagnostics {
  framesAnalyzed: number;
  peakDbfs: number;
  finalNoiseFloorDb: number;
  speechFrames: number;
}

function computeFrameDbfs(audio: Float32Array, offset: number, length: number): number {
  let sumSq = 0;
  for (let i = 0; i < length; i += 1) {
    const v = audio[offset + i];
    sumSq += v * v;
  }
  const rms = Math.sqrt(sumSq / length);
  return 20 * Math.log10(rms + 1e-10);
}

interface EnergyVadAnalysis {
  speechStartFrame: number;
  speechEndFrame: number;
  diagnostics: EnergyVadDiagnostics;
}

function analyzeEnergyVad(audio16k: Float32Array): EnergyVadAnalysis {
  let noiseFloorDb = ABSOLUTE_SILENCE_DB;
  let state: EnergyVadState = "silence";
  let candidateLoudMs = 0;
  let silenceMs = 0;
  let speechStartFrame = -1;
  let speechEndFrame = -1;
  let lastLoudFrame = -1;
  let speechFrames = 0;
  let peakDbfs = -Infinity;
  const frameCount = Math.floor(audio16k.length / FRAME_SAMPLES);

  for (let frameIdx = 0; frameIdx < frameCount; frameIdx += 1) {
    const offset = frameIdx * FRAME_SAMPLES;
    const dbfs = computeFrameDbfs(audio16k, offset, FRAME_SAMPLES);
    if (dbfs > peakDbfs) peakDbfs = dbfs;

    // Adaptive noise floor: drift down toward observed quiet, up very slowly
    // toward sustained noise. Clamp at the absolute silence/speech bounds.
    if (dbfs < noiseFloorDb + 3) {
      noiseFloorDb += (dbfs - noiseFloorDb) * NOISE_FLOOR_ATTACK_ALPHA;
    } else if (dbfs < ABSOLUTE_SPEECH_FLOOR_DB) {
      noiseFloorDb += (dbfs - noiseFloorDb) * NOISE_FLOOR_RELEASE_ALPHA;
    }
    if (noiseFloorDb < ABSOLUTE_SILENCE_DB) noiseFloorDb = ABSOLUTE_SILENCE_DB;
    if (noiseFloorDb > ABSOLUTE_SPEECH_FLOOR_DB) noiseFloorDb = ABSOLUTE_SPEECH_FLOOR_DB;

    const positiveThresh = noiseFloorDb + POSITIVE_MARGIN_DB;
    const negativeThresh = noiseFloorDb + NEGATIVE_MARGIN_DB;
    const isLoud = dbfs > positiveThresh;
    const isSoft = dbfs < negativeThresh;

    if (isLoud) {
      lastLoudFrame = frameIdx;
      speechFrames += 1;
    }

    if (state === "silence") {
      if (isLoud) {
        state = "possible_speech";
        candidateLoudMs = FRAME_MS;
      }
    } else if (state === "possible_speech") {
      if (isLoud) {
        candidateLoudMs += FRAME_MS;
        if (candidateLoudMs >= MIN_SPEECH_MS) {
          state = "speech";
          if (speechStartFrame < 0) {
            // Anchor speech start at the frame where the candidate began.
            const candidateFrames = Math.ceil(MIN_SPEECH_MS / FRAME_MS);
            speechStartFrame = Math.max(0, frameIdx - candidateFrames + 1);
          }
        }
      } else {
        state = "silence";
        candidateLoudMs = 0;
      }
    } else if (state === "speech") {
      if (isSoft) {
        state = "redemption";
        silenceMs = FRAME_MS;
      } else {
        silenceMs = 0;
      }
    } else if (state === "redemption") {
      if (isSoft) {
        silenceMs += FRAME_MS;
        if (silenceMs >= REDEMPTION_MS) {
          // End of the (first) speech segment in this batch.
          state = "silence";
          speechEndFrame = lastLoudFrame;
          break;
        }
      } else {
        state = "speech";
        silenceMs = 0;
      }
    }
  }

  // If audio ran out while we were still in speech / redemption, the speech
  // extends to the last loud frame we saw.
  if (speechStartFrame >= 0 && speechEndFrame < 0) {
    speechEndFrame = lastLoudFrame;
  }

  return {
    speechStartFrame,
    speechEndFrame,
    diagnostics: {
      framesAnalyzed: frameCount,
      peakDbfs,
      finalNoiseFloorDb: noiseFloorDb,
      speechFrames,
    },
  };
}

function dbToPseudoProbability(dbfs: number, noiseFloorDb: number): number {
  // The legacy contract returns probability in [0,1]. Map dB margin over the
  // adaptive floor onto that range so existing callers don't break and so
  // the smoke-record stats remain meaningful at a glance: speech bursts
  // saturate near 1.0, silence sits near 0.
  const margin = dbfs - noiseFloorDb;
  const normalized = margin / (POSITIVE_MARGIN_DB * 2);
  if (normalized <= 0) return 0;
  if (normalized >= 1) return 1;
  return normalized;
}

export async function detectSpeechWithEnergy(
  audio16k: Float32Array,
): Promise<VadDetectionResult> {
  if (audio16k.length < FRAME_SAMPLES) {
    throw new Error("Captured audio is too short for energy VAD");
  }

  const { speechStartFrame, speechEndFrame, diagnostics } = analyzeEnergyVad(audio16k);

  if (speechStartFrame < 0 || speechEndFrame < 0) {
    throw new Error(
      `Energy VAD detected no speech: peak_dbfs=${diagnostics.peakDbfs.toFixed(1)} ` +
        `noise_floor=${diagnostics.finalNoiseFloorDb.toFixed(1)} ` +
        `speech_frames=${diagnostics.speechFrames}`,
    );
  }

  const startFrame = Math.max(0, speechStartFrame - START_PAD_FRAMES);
  const endFrame = Math.min(diagnostics.framesAnalyzed - 1, speechEndFrame + END_PAD_FRAMES);
  const startSample = startFrame * FRAME_SAMPLES;
  const endSample = Math.min(audio16k.length, (endFrame + 1) * FRAME_SAMPLES);
  const trimmedAudio = audio16k.slice(startSample, endSample);

  if (trimmedAudio.length < 1600) {
    throw new Error("Speech region was too short after VAD trimming");
  }

  const peakProbability = dbToPseudoProbability(
    diagnostics.peakDbfs,
    diagnostics.finalNoiseFloorDb,
  );
  const averageProbability = dbToPseudoProbability(
    (diagnostics.peakDbfs + diagnostics.finalNoiseFloorDb) / 2,
    diagnostics.finalNoiseFloorDb,
  );

  return {
    averageProbability: Number(averageProbability.toFixed(4)),
    endSample,
    peakProbability: Number(peakProbability.toFixed(4)),
    startSample,
    trimmedAudio,
    voicedWindows: diagnostics.speechFrames,
  };
}

// Sanity-check helper for the smoke probe: runs the analyzer on synthetic
// silence and asserts that no speech is detected. Returns the diagnostics so
// the smoke record can include them.
export function checkEnergyVadOnSilence(): EnergyVadDiagnostics {
  // 1.5 s of pure silence at 16 kHz.
  const silence = new Float32Array(SAMPLE_RATE * 1.5);
  const { speechStartFrame, diagnostics } = analyzeEnergyVad(silence);
  if (speechStartFrame >= 0) {
    throw new Error(
      `Energy VAD false-positive on synthetic silence: start_frame=${speechStartFrame} ` +
        `peak_dbfs=${diagnostics.peakDbfs.toFixed(1)}`,
    );
  }
  return diagnostics;
}
